import React, { useState } from "react";
import { Mutation, Query } from "react-apollo";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import queries from "../../../queries";
import { agenda2Themes } from "@treeosk/common";
import FileUploader from "../../uploaders/FileUploader";
import ImageUploader from "../../uploaders/ImageUploader";
import PaletteColorPicker from "../../PaletteColorPicker";
import EditorSection from "./EditorSection";
import Agendas2EventsEditor from "./Agendas2EventsEditor";
import Agendas2DaysEditor from "./Agendas2DaysEditor";

// Editor of the "Agenda 2.0" cell (cell_type 'agendas2'). Its data (days / categories / events) is the
// same as the agendas cell and is imported with the same CSV endpoint.

const COLOR_GROUPS = [
  {
    id: 'module',
    title: 'Module',
    fields: [
      ['background_color', 'Fond du module'],
      ['text_color', 'Couleur du texte'],
    ],
  },
  {
    id: 'category_filter',
    title: 'Filtre par catégorie',
    fields: [
      ['background_color_filter_category', 'Fond du filtre'],
      ['background_color_filter_category_checked', 'Fond de la catégorie sélectionnée'],
      ['text_color_category_filter', 'Texte des catégories'],
      ['text_color_category_filter_selected', 'Texte de la catégorie sélectionnée'],
      ['border_color_selected_category', 'Bordure de la catégorie sélectionnée'],
    ],
  },
  {
    id: 'day_filter',
    title: 'Filtre par jour',
    fields: [
      ['background_color_filter_day', 'Fond du filtre'],
      ['background_color_filter_day_checked', 'Fond du jour sélectionné'],
      ['text_color_day_filter', 'Texte des jours'],
      ['text_color_day_filter_selected', 'Texte du jour sélectionné'],
      ['border_color_selected_day', 'Bordure du jour sélectionné'],
    ],
  },
  {
    id: 'events',
    title: 'Évènements',
    fields: [
      ['text_color_time', "Couleur de l'heure"],
      ['text_color_event_name', "Couleur du nom"],
      ['text_color_event_details', 'Couleur des détails (intervenant, lieu, langue)'],
      ['background_color_button_open', "Fond du bouton d'ouverture"],
      ['text_color_button_open', "Texte du bouton d'ouverture"],
    ],
  },
  {
    id: 'timeline',
    title: 'Chronologie (cartes et trait)',
    fields: [
      ['color_timeline', 'Couleur du trait vertical'],
      ['background_color_event', 'Fond des cartes'],
      ['text_color_card', 'Couleur du titre des cartes'],
      ['text_color_card_details', 'Couleur du statut et des détails des cartes'],
    ],
  },
  {
    id: 'popup',
    title: 'Popup',
    fields: [
      ['background_color_pop_up', 'Fond de la popup'],
      ['text_color_description', 'Couleur de la description'],
      ['color_button_close', 'Couleur du bouton de fermeture'],
    ],
  },
];

const SIZE_FIELDS = [
  ['font_size_name', "Taille du nom de l'évènement (em)"],
  ['font_size_time', "Taille de l'heure (em)"],
  ['font_size_details', 'Taille des détails (em)'],
  ['font_size_popup', 'Taille du texte de la popup (em)'],
  ['font_size_filter_category', 'Taille du filtre par catégorie (em)'],
  ['font_size_filter_day', 'Taille du filtre par jour (em)'],
];

const BOOLEAN_FIELDS = [
  ['hide_past_events', 'Masquer les évènements et les jours passés'],
  ['show_all_tab', 'Afficher un onglet « Tous » dans les catégories'],
  ['show_end_time', "Afficher l'heure de fin"],
  ['subtitle_new_line', 'Texte à côté du titre : à la ligne (sous le titre)'],
  ['bolder_radius_filter_day', 'Filtre par jour arrondi'],
  ['bolder_radius_filter_category', 'Filtre par catégorie arrondi'],
];

const NUMBER_FIELDS = [...SIZE_FIELDS.map(([key]) => key)];
const COLOR_KEYS = _.flatMap(COLOR_GROUPS, (group) => group.fields.map(([key]) => key));
const ALL_KEYS = [...COLOR_KEYS, ...NUMBER_FIELDS, ...BOOLEAN_FIELDS.map(([key]) => key), 'list_max_height_vh', 'layout', 'background_image_uri', 'color_theme', 'background_fit'];

const BACKGROUND_FITS = [
  ['cover', 'Remplir la cell (l\'image peut être recadrée)'],
  ['contain', "Image entière (des bords peuvent rester vides)"],
  ['stretch', 'Étirer à la taille de la cell'],
];

const COLOR_THEMES = [
  ['custom', 'Personnalisé'],
  ['euroseeds', 'Euroseeds (couleurs et fond)'],
];

const LAYOUTS = [
  ['timeline', 'Chronologie (heure à gauche, trait vertical, cartes)'],
  ['cards', 'Cartes'],
];

// Every color field of the agenda 2.0 offers the same three swatches: white, black and the Euroseeds green.
// Blanc, noir, vert et violet Euroseeds (le même violet que dans les thèmes de agenda2Themes.js)
const COLOR_PALETTE = ['#FFFFFF', '#000000', '#A6C82A', '#654455'];

const label = (key, defaultMessage) => (
  <FormattedMessage id={`agendas2.edit.${key}`} defaultMessage={defaultMessage} />
);

const toRgba = ({ rgb }) => `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})`;

function Agendas2CellEditor({ cell, language }) {
  if (!cell || !cell.id) {
    return null;
  }

  return (
    <Query query={queries.cells.agendas2.get} variables={{ id: cell.id }}>
      {({ loading, error, data, refetch }) => {
        if (loading) return null;
        if (error) return `Error!: ${error}`;

        const { agenda2Cell } = data;
        if (!agenda2Cell) return null;

        if (language) {
          return (
            <Mutation
              mutation={queries.agenda_category.updateI18n}
              refetchQueries={() => [{ query: queries.cells.agendas2.get, variables: { id: cell.id } }]}
            >
              {(update) => agenda2Cell.categories.map((category) => (
                <UpdateI18nCategory language={language} key={category.id} category={category} update={update} />
              ))}
            </Mutation>
          );
        }

        return (
          <Mutation
            mutation={queries.cells.agendas2.update}
            refetchQueries={() => [{ query: queries.cells.agendas2.get, variables: { id: cell.id } }]}
          >
            {(updateCell) => (
              <Agendas2CellEditorForm
                cell={agenda2Cell}
                updateCell={updateCell}
                refetch={refetch}
              />
            )}
          </Mutation>
        );
      }}
    </Query>
  );
}

function Agendas2CellEditorForm({ cell, updateCell, refetch }) {
  const [values, setValues] = useState(() => _.pick(cell, ALL_KEYS));
  const set = (key, value) => setValues((previous) => ({ ...previous, [key]: value }));
  // The Euroseeds theme fills in its colors (still editable afterwards) and turns its background image on.
  const setTheme = (theme) => setValues((previous) => ({
    ...previous,
    ...(theme === 'euroseeds' ? agenda2Themes.EUROSEEDS_COLORS : {}),
    color_theme: theme,
  }));

  const save = () => {
    const input = { id: cell.id, ...values };
    NUMBER_FIELDS.forEach((key) => { input[key] = parseFloat(values[key]); });
    input.list_max_height_vh = parseInt(values.list_max_height_vh, 10);
    updateCell({ variables: { input } });
  };

  return (
    <div className="tsk-editor-form">
      <EditorSection title={<FormattedMessage id="agendas2.edit.section.import" defaultMessage="Import CSV" />}>
        <div className="tsk-input-line tsk-input-line--full">
          <label><FormattedMessage id="plan.edit.label.csv" /></label>
          <FileUploader cellId={cell.id} accept={'.csv'} uploadPath='/csv_agenda/' onUpload={() => refetch()} />
        </div>
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agendas2.edit.section.days" defaultMessage="Jours (ajouter, changer la date)" />} defaultOpen={false}>
        <Agendas2DaysEditor cell={cell} refetch={refetch} />
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agendas2.edit.section.events" defaultMessage="Évènements (heures, textes, popup)" />} defaultOpen={false}>
        <Agendas2EventsEditor cell={cell} refetch={refetch} />
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agendas2.edit.section.background" defaultMessage="Fond" />} defaultOpen={false}>
        <p style={{ margin: '0 0 8px' }}>
          Ajoute ta propre image de fond quand tu en as besoin. Elle remplace le fond du thème Euroseeds ; retire-la
          pour retrouver le fond du thème (ou aucun fond). La couleur de fond se règle dans Couleurs &gt; Module.
        </p>
        <div className="tsk-input-line tsk-input-line--full">
          <label>{label('background_image_uri', 'Image de fond')}</label>
          <ImageUploader
            value={values.background_image_uri}
            canDelete
            onUpload={(uri) => set('background_image_uri', uri)}
            onDelete={() => set('background_image_uri', null)}
          />
        </div>
        <div className="tsk-input-line">
          <label>{label('background_fit', "Ajustement de l'image")}</label>
          <select value={values.background_fit || 'cover'} onChange={(e) => set('background_fit', e.target.value)}>
            {BACKGROUND_FITS.map(([value, text]) => (
              <option key={value} value={value}>{text}</option>
            ))}
          </select>
        </div>
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agendas2.edit.section.options" defaultMessage="Affichage" />}>
        <div className="tsk-input-line">
          <label>{label('layout', 'Format')}</label>
          <select value={values.layout || 'timeline'} onChange={(e) => set('layout', e.target.value)}>
            {LAYOUTS.map(([value, text]) => (
              <option key={value} value={value}>{text}</option>
            ))}
          </select>
        </div>
        {BOOLEAN_FIELDS.map(([key, text]) => (
          <div className="tsk-input-line" key={key}>
            <label>{label(key, text)}</label>
            <input type="checkbox" checked={!!values[key]} onChange={(e) => set(key, e.target.checked)} />
          </div>
        ))}
        <div className="tsk-input-line">
          <label>{label('list_max_height_vh', 'Hauteur maximale de la liste (% de la hauteur de l\'écran)')}</label>
          <input type="number" min="10" max="100" value={values.list_max_height_vh} onChange={(e) => set('list_max_height_vh', e.target.value)} />
        </div>
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agendas2.edit.section.colors" defaultMessage="Couleurs" />} defaultOpen={false}>
        <div className="tsk-input-line">
          <label>{label('color_theme', 'Thème de couleurs')}</label>
          <select value={values.color_theme || 'custom'} onChange={(e) => setTheme(e.target.value)}>
            {COLOR_THEMES.map(([value, text]) => (
              <option key={value} value={value}>{text}</option>
            ))}
          </select>
        </div>
        {COLOR_GROUPS.map((group) => (
          <EditorSection nested key={group.id} title={label(`group.${group.id}`, group.title)}>
            {group.fields.map(([key, text]) => (
              <div className="tsk-input-line" key={key}>
                <label>{label(key, text)}</label>
                <PaletteColorPicker
                  palette={COLOR_PALETTE}
                  allowNone
                  disableAlpha={false}
                  color={values[key]}
                  onChange={(color) => set(key, toRgba(color))}
                />
              </div>
            ))}
          </EditorSection>
        ))}
      </EditorSection>

      <EditorSection title={<FormattedMessage id="agendas2.edit.section.sizes" defaultMessage="Tailles" />} defaultOpen={false}>
        {SIZE_FIELDS.map(([key, text]) => (
          <div className="tsk-input-line" key={key}>
            <label>{label(key, text)}</label>
            <input type="number" step="0.1" min="0.3" value={values[key]} onChange={(e) => set(key, e.target.value)} />
          </div>
        ))}
      </EditorSection>

      <button className="tsk-button" onClick={save}>
        <FormattedMessage id={'common.button.submit'} />
      </button>
    </div>
  );
}

function UpdateI18nCategory({ category, update, language }) {
  const i18nValues = _.filter(category.i18n, { language_code: language.code })[0] || {};
  const [name, setName] = useState(i18nValues.name || '');

  return (
    <div>
      <label>{category.name}</label>
      <input type="text" className="input" value={name} onChange={(e) => setName(e.target.value)} />
      <button
        onClick={() => update({ variables: { input: { name, category_id: category.id, language_code: language.code } } })}
        className="tsk-button"
      >
        Update
      </button>
    </div>
  );
}

export default Agendas2CellEditor;
